import {
    useEffect,
    useRef
} from 'react';

import { debounce } from '$utilities/events';

/* ========================================================================= */
/* Keyboard */
/* ========================================================================= */

/**
 * @callback KeyboardEventCallback
 * @param {React.KeyboardEvent<HTMLElement>} event
 */

/**
 * Custom hook generating keyboard event listeners. Based on the method key names provided
 * @param {Object<string, KeyboardEventCallback>} watchers
 * @param {Array<any>} [dependencies] Dependencies used in useEffect.
 * @return {React.MutableRefObject<any>} The element to observe. If not provided, `window` will be used as a default.
 * @example
 *  // without assignment it to ref
 *  useKeyboard({
 *       'Escape': (e) => console.log('Pressed `Esc` key')
 *   }, []);
 * @example
 *  // with ref
 *  const { elementRef } = useKeyboard({
 *      'a': (e) => console.log('Pressed `a` key')
 *  })
 */
export const useKeyboard = (watchers, dependencies = []) => {
    const elementRef = useRef(null);

    /**@param {React.KeyboardEvent<HTMLElement>} event */
    const handleKeyDown = event => {
        if (watchers?.[event.key] && typeof watchers[event.key] === 'function') {
            watchers[event.key](event);
        }
    };

    useEffect(() => {
        const target = elementRef.current || window;
        target.addEventListener('keydown', handleKeyDown);
        return () => target.removeEventListener('keydown', handleKeyDown);
    }, [ watchers, elementRef, elementRef.current, ...dependencies ]);

    return { elementRef };
};

/**
 * @hook useFocusTrap
 * @param {boolean} [isEnabled=true] Sets to false if the component remains as invisible element in the DOM.
 * @return {React.MutableRefObject<any>}
 *
 * Traps the focus state within the referenced element (eg.: useful for dialogs).
 * 1. If the next focused element belongs to the container, escape.
 * 2. Otherwise:
 * 2.1. Focus the last element if coming from the first focusable element.
 * 2.2. Focus the first element (if available).
 * 2.3. If there is no focusable elements, focus the container.
 */
export const useFocusTrap = (isEnabled = true) => {
    const elementRef = useRef(null);
    const focusableElementsSelector = [ 'button', 'a', 'input', 'textarea', 'select', '[tabindex="0"]' ].join(',');
    const getFocusableElements = element => element.querySelectorAll(focusableElementsSelector);

    useEffect(() => {
        if (!elementRef.current || !isEnabled) {
            return;
        }

        const preventEscapes = e => {
            const originElement = e.relatedTarget;
            const targetElement = e.target;

            if (elementRef.current?.contains(targetElement)) {
                return; /* [1] */
            }

            /* [2] */
            const focusableElements = getFocusableElements(elementRef.current);
            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement = focusableElements[focusableElements.length - 1];

            if (originElement?.isSameNode(firstFocusableElement)) { /* [2.1] */
                lastFocusableElement.focus();
            } else if (firstFocusableElement) { /* [2.2] */
                firstFocusableElement.focus();
            } else { /* [2.3] */
                elementRef.current.focus();
            }
        };

        window.addEventListener('focus', preventEscapes, true);
        return () => window.removeEventListener('focus', preventEscapes, true);
    }, [ elementRef, isEnabled ]);

    return {
        elementRef
    };
};

/**
 * @hook useFocusWithin
 * @param {function} callback
 *     The callback will be executed each time a focus interfaction is observed.
 * @return {React.MutableRefObject<any>}
 *
 * Watches a target element and provide the callback with useful information.
 * Reproduces the same behaviour as a `:focus-within` CSS pseudo selector.
 * 1. The asynchronous execution ensures priority to any other event (clicks, etc.).
 * 2. Prevent calling `contains()` on Window. (When the browser window is defocused it returns to the Window element)
 *
 * @example: const elementRef = useFocusWithin(() => ...)
 */
export const useFocusWithin = callback => {
    const elementRef = useRef(null);

    useEffect(() => {
        if (!elementRef.current) {
            return;
        }

        /* [1] */
        const detectFocus = debounce(e => {
            const fromElement = e.relatedTarget;
            const toElement = e.target;
            /* [2] */
            if (toElement === window) { return; }

            const wasFocused =  elementRef.current.contains(fromElement);
            const isFocused = elementRef.current.contains(toElement);

            if (wasFocused !== isFocused) {
                callback({ fromElement, toElement, isFocused });
            }
        }, 50);

        window.addEventListener('focus', detectFocus, true);
        return () => window.removeEventListener('focus', detectFocus, true);
    }, [ elementRef.current ]);

    return elementRef;
};
